// Recording a death (docs/plans/phase2-mortuary-and-notifications.md §1.1, §5). Pure rules the
// API applies before it writes anything: where a death can be recorded from, when, and which
// causes a record needs. Causes themselves are ICD-11 codes from the configured terminology;
// nothing here knows a code.

import { err, ok, type Result } from '../result.ts';

/**
 * Where the person died. `outside_facility` is a body brought to the mortuary after a death
 * elsewhere: certified elsewhere, so no certifier, cause or manner is recorded here.
 */
export const DEATH_PLACES = ['ward', 'casualty', 'brought_in_dead', 'outside_facility'] as const;
export type DeathPlace = (typeof DEATH_PLACES)[number];

/**
 * TODO(clinical-verify) TODO(legal-verify) D3: the manner-of-death list, provisional. It
 * follows the WHO certificate's list as we understand it; the official Kenyan form may differ.
 */
export const DEATH_MANNERS = [
  'disease',
  'accident',
  'intentional_self_harm',
  'assault',
  'legal_intervention',
  'war',
  'pending_investigation',
  'could_not_be_determined',
] as const;
export type DeathManner = (typeof DEATH_MANNERS)[number];

export const DEATH_CAUSE_ROLES = ['immediate', 'underlying', 'contributing'] as const;
export type DeathCauseRole = (typeof DEATH_CAUSE_ROLES)[number];

export const DEATH_RECORD_STATUSES = ['recorded', 'entered_in_error'] as const;
export type DeathRecordStatus = (typeof DEATH_RECORD_STATUSES)[number];

/** The visit types a death is recorded from, as the database names them. */
type AnyVisitType =
  'outpatient' | 'walk_in' | 'inpatient' | 'emergency' | 'mortuary' | 'counter_sale';

/**
 * The places a death can be recorded as, from a visit of this type. A stay is a ward death;
 * casualty offers casualty or brought in dead. TODO(clinical-verify) D9: outpatient and
 * walk-in visits offer none (record from casualty).
 */
export function deathPlacesForVisit(type: AnyVisitType): readonly DeathPlace[] {
  if (type === 'inpatient') return ['ward'];
  if (type === 'emergency') return ['casualty', 'brought_in_dead'];
  return [];
}

export type DeathTimeErrorCode =
  'death_time.future' | 'death_time.before_visit' | 'death_time.before_location';

/**
 * The time of death is a clinical event time (domain rule 5): not in the future, not before
 * the visit began, and not before `notBefore` (the start of the patient's current bed, so a
 * stay is never discharged before its last move).
 */
export function checkDeathTime(
  diedAt: Date,
  visitStartedAt: Date,
  now: Date,
  notBefore?: Date,
): Result<Date, DeathTimeErrorCode> {
  if (diedAt.getTime() > now.getTime()) return err('death_time.future');
  if (diedAt.getTime() < visitStartedAt.getTime()) return err('death_time.before_visit');
  if (notBefore !== undefined && diedAt.getTime() < notBefore.getTime()) {
    return err('death_time.before_location');
  }
  return ok(diedAt);
}

export type DeathCauseErrorCode =
  | 'death_cause.underlying_required'
  | 'death_cause.one_per_role'
  | 'death_cause.not_certified_here'
  | 'death_cause.manner_required';

/**
 * A certified death has a manner, at most one immediate and one underlying cause, and any
 * number of contributing conditions. TODO(clinical-verify) D4: the underlying cause is
 * required unless the manner is "pending investigation". A death recorded elsewhere carries
 * no causes and no manner here.
 */
export function checkDeathCauses(
  causes: readonly { readonly role: DeathCauseRole }[],
  manner: DeathManner | null,
  place: DeathPlace,
): Result<true, DeathCauseErrorCode> {
  if (place === 'outside_facility') {
    return causes.length === 0 && manner === null
      ? ok(true)
      : err('death_cause.not_certified_here');
  }
  if (manner === null) return err('death_cause.manner_required');
  const count = (role: DeathCauseRole) => causes.filter((c) => c.role === role).length;
  if (count('immediate') > 1 || count('underlying') > 1) return err('death_cause.one_per_role');
  if (count('underlying') === 0 && manner !== 'pending_investigation') {
    return err('death_cause.underlying_required');
  }
  return ok(true);
}
